/**
 * Accessibility Display — full-screen captions for projector / LED wall.
 * Doubles as an OBS browser source.
 *
 * URL: /event/:eventId/accessibility/display?lang=en&size=72&theme=dark
 *
 * Supports query overrides so a stagehand can paste a custom URL straight
 * into OBS without using a controls panel.  Falls back to the event default
 * language.  Renders only the most recent N captions to keep the screen
 * easy to read from the back of the room.
 */
import React, { useEffect, useRef, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';

const API = process.env.REACT_APP_BACKEND_URL;

const AccessibilityDisplay = () => {
  const { eventId } = useParams();
  const [params] = useSearchParams();
  const lang = params.get('lang') || 'en';
  const fontSize = Number(params.get('size')) || 64;
  const theme = params.get('theme') || 'dark';
  const maxLines = Number(params.get('lines')) || 3;
  const overlay = params.get('overlay') === '1';

  const [state, setState] = useState(null);
  const [segments, setSegments] = useState([]);
  const wsRef = useRef(null);

  useEffect(() => {
    let alive = true;
    const fetchState = async () => {
      try {
        const r = await fetch(`${API}/api/accessibility/events/${eventId}/state`);
        if (!r.ok) return;
        const data = await r.json();
        if (alive) setState(data);
      } catch (_e) { /* network blip — retry on next interval */ }
    };
    fetchState();
    const t = setInterval(fetchState, 8000);
    return () => { alive = false; clearInterval(t); };
  }, [eventId]);

  const sessionId = state?.active_session?.id;

  useEffect(() => {
    if (!sessionId) return;
    const url = `${API.replace(/^http/, 'ws')}/api/accessibility/ws/captions/${sessionId}/${lang}`;
    const ws = new WebSocket(url);
    wsRef.current = ws;
    ws.onmessage = (evt) => {
      try {
        const msg = JSON.parse(evt.data);
        if (msg.type === 'segment' && msg.text) {
          setSegments(prev => {
            if (prev.some(s => s.id === msg.id)) return prev;
            return [...prev, msg].slice(-maxLines);
          });
        }
      } catch (_e) { /* malformed frame — drop */ }
    };
    ws.onclose = () => { /* will reconnect on next state poll */ };
    return () => { try { ws.close(); } catch (_e) { /* already closed */ } };
  }, [sessionId, lang, maxLines]);

  const containerClass = overlay
    ? 'min-h-screen bg-transparent text-white p-12 flex flex-col justify-end'
    : theme === 'light'
      ? 'min-h-screen bg-white text-slate-900 p-12 flex flex-col justify-end'
      : 'min-h-screen bg-black text-white p-12 flex flex-col justify-end';

  return (
    <div className={containerClass} data-testid="accessibility-display">
      {/* Subtle status pill — hidden when overlay mode */}
      {!overlay && (
        <div className="absolute top-4 right-6 text-xs opacity-50">
          {state?.event_name ? `${state.event_name} · ${lang.toUpperCase()}` : '…'}
        </div>
      )}

      <div className="space-y-6">
        {!sessionId && (
          <p className="opacity-60" style={{ fontSize: `${fontSize}px` }}>Standing by…</p>
        )}
        {segments.map(s => (
          <p key={s.id} className="leading-tight font-medium"
             style={{ fontSize: `${fontSize}px`, textShadow: overlay ? '2px 2px 8px rgba(0,0,0,0.85)' : 'none' }}
             data-testid={`accessibility-display-segment-${s.id}`}>
            {s.text}
          </p>
        ))}
      </div>
    </div>
  );
};

export default AccessibilityDisplay;
